import { Stack } from '@mui/material';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';

import { EditStationForm } from '@/components/management/form/EditStationForm';
import { FormGridLayout } from '@/components/management/form/formHelpers/FormGridLayout';
import { AdministrationBody } from '@/components/management/layout/AdministrationBody';
import { AdministrationHeader } from '@/components/management/layout/AdministrationHeader';
import { ParkAndRideFacilitiesTable } from '@/components/management/table/ParkAndRideFacilitiesTable';
import { ButtonLink } from '@/components/router/ButtonLink';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import {
  backendApiParkAndRideFacilitiesFindAllOptions,
  backendApiStationsFindOneOptions,
  backendApiStationsRemoveMutation,
  backendApiStationsUpdateMutation,
} from '@/modules/api/backend/@tanstack/react-query.gen';
import { useConfirmStore } from '@/stores/useConfirmStore';
import { useSnackbarStore } from '@/stores/useSnackbarStore';

export const Route = createFileRoute('/_authenticated/management/stations/$stationId/')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  beforeLoad: () => ({ breadcrumb: null }),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { stationId } = Route.useParams();
  const isAdmin = useIsAdmin();

  const { data: stationData } = useSuspenseQuery(
    backendApiStationsFindOneOptions({
      path: { stationId },
      query: { parkAndRideFacilities: true, parkAndRideFacilitiesPublication: false },
    }),
  );

  const { data: facilities } = useQuery(
    backendApiParkAndRideFacilitiesFindAllOptions({
      query: {
        stationId,
      },
    }),
  );

  const { openSnackbar } = useSnackbarStore((state) => state.actions);
  const { openConfirm } = useConfirmStore((state) => state.actions);

  const { mutateAsync: mutateAsyncUpdate } = useMutation({
    ...backendApiStationsUpdateMutation(),
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Die Änderungen wurden erfolgreich gespeichert!',
      });
    },
    onError: () => {
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Speichern.',
      });
    },
  });

  const { mutateAsync: mutateAsyncDelete } = useMutation({
    ...backendApiStationsRemoveMutation(),
    meta: { invalidateQueries: false },
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Die Station wurde erfolgreich gelöscht!',
      });
      navigate({
        to: '/management/stations',
        replace: true,
      });
    },
    onError: () =>
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Löschen der Station.',
      }),
  });

  const tabs = ['Station', 'Bike and Ride', 'P+R Anlagen'];
  const { setRef, scrollTo, activeIndex } = useScrollSpy<HTMLDivElement>(tabs.length);

  return (
    <Fragment>
      <AdministrationHeader
        formId={isAdmin ? 'stationForm' : undefined}
        label="Station Bearbeiten"
        {...(isAdmin
          ? {
              links: [
                {
                  name: 'Auf Karte anzeigen',
                  options: {
                    to: '/',
                    search: (prev) => ({ ...prev, popup: true, id: stationId }),
                  },
                  icon: 'MAP_PIN',
                },

                {
                  name: 'Bearbeitungsverlauf',
                  options: {
                    to: '/management/stations/$stationId/audits',
                  },
                  icon: 'CLOCK',
                },
              ],
            }
          : {
              links: [
                {
                  name: 'Auf Karte anzeigen',
                  options: {
                    to: '/',
                    search: (prev) => ({ ...prev, popup: true, id: stationId }),
                  },
                  icon: 'MAP_PIN',
                },
              ],
            })}
        onDelete={
          isAdmin
            ? async () => {
                const confirmed = await openConfirm({
                  title: 'Station löschen',
                  message: 'Sind Sie sicher, dass Sie diese Station unwiderruflich löschen möchten?',
                  confirmText: 'Löschen',
                  cancelText: 'Abbrechen',
                });

                if (confirmed) {
                  await mutateAsyncDelete({
                    path: { stationId },
                  });
                }
              }
            : undefined
        }
      />
      <AdministrationBody>
        <FormGridLayout activeTab={activeIndex} onTabChange={scrollTo} tabs={tabs}>
          <Stack spacing={2}>
            <EditStationForm
              disabled={!isAdmin}
              initialValues={stationData}
              setRef={setRef}
              tabs={tabs}
              onSubmit={async (values) => {
                await mutateAsyncUpdate({ path: { stationId }, body: values });
              }}
            />
            {facilities ? (
              <ParkAndRideFacilitiesTable
                facilities={facilities}
                setRef={setRef}
                stationId={stationId}
                title="P+R Anlagen"
              />
            ) : null}
            {stationId ? (
              <Stack alignItems="flex-end">
                <ButtonLink
                  color="primary"
                  params={{ stationId }}
                  to="/management/stations/$stationId/park-and-ride-facilities/create"
                  variant="contained"
                >
                  Parkraumangebot hinzufügen
                </ButtonLink>
              </Stack>
            ) : null}
          </Stack>
        </FormGridLayout>
      </AdministrationBody>
    </Fragment>
  );
}
